/** The session write model (create, message-append, lifecycle). */

import type {
  AnyDocumentId,
  DocHandle,
  Repo,
} from '@automerge/automerge-repo/slim'
import { v4 as uuidv4 } from 'uuid'
import { BranchLog, initThread, THREAD_KEY, writeEntry } from '../../log'
import type {
  EntryId,
  EntryPayload,
  MessageRole,
  TimestampMs,
  ToolUseBlock,
} from '../../log/types'
import type { WrittenOn } from '../../log/writes'
import {
  mapWithConflicts,
  removeCommentIndexEntries,
  writeCommentIndex,
} from '../comments/store'
import type { CommentIndexEntry } from '../comments/types'
import { findProjectForWrite } from '../project/mutations'
import { changeProject, projectString } from '../project/scalars'
import { imm } from '../stored-string'
import {
  createCatalogSession,
  findSessionCatalogForWrite,
  movePrivateSessionCatalog,
  usesPrivateSessionCatalog,
} from './catalog'
import { sessionAddedProjects } from './reads'
import { awaitingName } from './title'
import type {
  SessionDoc,
  SessionMetaEntry,
  SessionRunStatus,
  SessionTitleSetBy,
} from './types'
import { HOLDING_MAX } from './types'

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type Draft = any
/**
 * A message to append. Beyond the text, an assistant message can carry `toolUses`
 * (the tool-call blocks it issued) and a `tool_result` message carries `toolUseId`
 * (the call it answers) + `isError` — so an imported transcript keeps its tool
 * interactions instead of collapsing to plain text.
 */
export interface MessageInput {
  role: MessageRole
  text: string
  toolUses?: ToolUseBlock[]
  toolUseId?: string
  isError?: boolean
  /** DETERMINISTIC entry id (e.g. derived from a source line's uuid). When set,
   *  re-importing the same message OVERWRITES its entry rather than appending a
   *  duplicate — makes an at-least-once importer idempotent. Falls back to a random
   *  uuid when absent. */
  id?: EntryId
  /** DETERMINISTIC ordering key (e.g. the source line's timestamp in ms). Entries
   *  order by `(createdAt, id)`, both deterministic, so a re-import reproduces the
   *  same order. Falls back to a monotonic clock when absent. */
  createdAt?: TimestampMs
  /** On a write's tool_result: where the file landed (RFC 0329 3a). Stored in
   *  the message's `metadata`. */
  writtenOn?: WrittenOn
  /** RFC 0307 3c: a line said on this voice call. Written with no run. */
  voiceCallId?: string
  spokenBy?: 'person' | 'voice'
}
/**
 * Address this session's runs to a device (RFC 0261 slice 3 placement).
 *
 * Appends a whiffletree `worker_migration` whose `workerId` becomes the thread's
 * `selected_tool_worker` — the device that may claim its runs. This is the
 * whiffletree-native mechanism; there is no per-run device field. No-op if the
 * device is already selected, so re-picking the same one does not append a
 * redundant migration. A composer's device picker calls this on selection.
 */
export async function selectSessionDevice(
  repo: Repo,
  sessionId: string,
  deviceId: string,
): Promise<void> {
  const handle = await repo.find<SessionDoc>(sessionId as AnyDocumentId)
  const log = new BranchLog(handle as never)
  const hs = log.heads()
  const current = hs.length > 0 ? log.activeToolWorker(hs[0]) : null
  if (current === deviceId) return
  log.migrateWorker(deviceId, {
    ...(current ? { fromWorkerId: current } : {}),
    reason: 'placement',
  })
}
/** Initialize a newly created session before adding it to its catalog. */
export function initializeSession(
  session: DocHandle<SessionDoc>,
  projectId: string,
  opts: Parameters<typeof createSession>[2],
  now = Date.now(),
): void {
  const sessionId = session.documentId
  const title = opts?.title ?? 'New Session'
  session.change((d) => {
    d.id = sessionId
    d.title = title
    d.createdAt = now
    d.updatedAt = now
    d.status = 'active'
    if (opts?.sourceId) d.sourceId = opts.sourceId
    // Root-of-trust so thread paging can seal (ThreadSealer abstains without it).
    // Defaults to the owning project id — a stable, always-present value.
    d.createdBy = opts?.createdBy ?? projectId
    // Init the RFC 0215 thread inline so the session doc is complete from birth.
    initThread(d as unknown as Record<string, unknown>)
  })
}

/**
 * Create a session doc (log initialised inline) and register it into the
 * project doc. Async because registering needs the project handle
 * (`repo.find`). Returns the session handle.
 */
export async function createSession(
  repo: Repo,
  projectId: string,
  opts?: {
    title?: string
    sourceId?: string
    createdBy?: string
    /** The session that opened this one, when an agent did (`start_session`).
     *  Registry-only, so a list can keep a sub-session out of the top level
     *  without opening a single session document. */
    parentSessionId?: string
    /** Who chose `title` (RFC 0311 3b). Defaults to `'stub'`, the placeholder
     *  the agent's one-time rename replaces. A title an agent or a person chose
     *  on purpose says so, and is never renamed. */
    titleSetBy?: SessionTitleSetBy
  },
): Promise<DocHandle<SessionDoc>> {
  if (usesPrivateSessionCatalog(repo))
    return createCatalogSession(repo, projectId, opts)
  const title = opts?.title ?? 'New Session'
  const titleSetBy = opts?.titleSetBy ?? 'stub'
  const now = Date.now()

  const session = repo.create<SessionDoc>()
  const sessionId = session.documentId
  initializeSession(session, projectId, opts, now)

  // Register into the project registry. In-place array/map writes (never a
  // whole-value reassign) so concurrent registrations merge losslessly.
  const project = await findProjectForWrite(repo, projectId)
  changeProject(project, (d) => {
    if (!d.sessionIds) d.sessionIds = []
    if (!d.sessionIds.some((id) => projectString(id) === sessionId))
      d.sessionIds.push(sessionId)
    if (!d.sessionMeta) d.sessionMeta = {}
    d.sessionMeta[sessionId] = {
      // One op, not one per character: a title is assigned whole when it
      // changes, never typed into together. See `stored-string`.
      title: imm(title),
      titleSetBy,
      updatedAt: now,
      ...(opts?.sourceId ? { sourceId: opts.sourceId } : {}),
      ...(opts?.parentSessionId
        ? { parentSessionId: opts.parentSessionId }
        : {}),
    }
  })

  return session
}
/**
 * Record how many source turns have been imported for a session (in the project
 * registry), so an incremental importer/watcher can resume and append only the
 * delta. Registry-only write — no session hydration.
 */
export async function setSessionSourceTurns(
  repo: Repo,
  projectId: string,
  sessionId: string,
  sourceTurns: number,
): Promise<void> {
  const project = await findSessionCatalogForWrite(repo, projectId, sessionId)
  project.change((d) => {
    if (!d.sessionMeta) d.sessionMeta = {}
    // Mutate in place — re-assigning an existing doc object throws in Automerge.
    const existing = d.sessionMeta[sessionId]
    if (existing) existing.sourceTurns = sourceTurns
    else d.sessionMeta[sessionId] = { sourceTurns }
  })
}
/**
 * Rename a session: update its own doc title and the project registry's
 * `sessionMeta` entry (both in place, so concurrent edits merge). Async because
 * it opens both handles.
 */
export async function renameSession(
  repo: Repo,
  projectId: string,
  sessionId: string,
  title: string,
  /** Who is naming it (RFC 0311 3b). Required, so no caller writes a title
   *  without saying whose it is: a person's rename passes `'you'`. */
  setBy: SessionTitleSetBy,
): Promise<void> {
  const project = await findSessionCatalogForWrite(repo, projectId, sessionId)
  const now = Date.now()
  const session = await repo.find<SessionDoc>(sessionId as AnyDocumentId)
  session.change((d) => {
    d.title = title
    d.updatedAt = now
  })
  project.change((d) => writeRenamedTitle(d, sessionId, title, setBy, now))
}
/**
 * The agent's one-time name (RFC 0311 3b): writes `agentTitle` only while the
 * session is still waiting for a name. Returns whether it wrote.
 *
 * NEVER `title`. The guard and the write are one change, which is atomic on
 * this peer, but it cannot see a rename that has not synced here yet. So the
 * agent writes a key a person never writes, and `sessionTitleOf` lets a
 * person's `title` win whenever `titleSetBy` says it is theirs. Merged in
 * either order, the person keeps their name. The session document's own
 * title is left alone for the same reason: nothing reads it for display.
 */
export async function nameSessionOnce(
  repo: Repo,
  projectId: string,
  sessionId: string,
  title: string,
): Promise<boolean> {
  let claimed = false
  const project = await findSessionCatalogForWrite(repo, projectId, sessionId)
  project.change((d) => {
    claimed = claimAgentTitle(d, sessionId, title, Date.now())
  })
  return claimed
}
/** The agent's write, on a project document inside a change. Pure, so a test
 *  can fork a document, run it beside a person's rename, and merge the two. */
export function claimAgentTitle(
  d: Draft,
  sessionId: string,
  title: string,
  now: number,
): boolean {
  const existing = d.sessionMeta?.[sessionId]
  if (!existing || !awaitingName(existing)) return false
  existing.agentTitle = title
  existing.updatedAt = now
  return true
}
/** A person's rename, on a project document inside a change. The registry half
 *  of `renameSession`, pure for the same reason as `claimAgentTitle`. */
export function writeRenamedTitle(
  d: Draft,
  sessionId: string,
  title: string,
  setBy: SessionTitleSetBy,
  now: number,
): void {
  if (!d.sessionMeta) d.sessionMeta = {}
  // Mutate in place — re-assigning an existing doc object throws in Automerge.
  const existing = d.sessionMeta[sessionId]
  if (existing) {
    existing.title = title
    existing.titleSetBy = setBy
    existing.updatedAt = now
  } else {
    d.sessionMeta[sessionId] = {
      title: imm(title),
      titleSetBy: setBy,
      updatedAt: now,
    }
  }
}
/** Record that the namer asked the model, once (RFC 0311 3b). False when it
 *  already did, or the session no longer needs a name. */
export async function markNameTried(
  repo: Repo,
  projectId: string,
  sessionId: string,
): Promise<boolean> {
  let marked = false
  const project = await findSessionCatalogForWrite(repo, projectId, sessionId)
  project.change((d) => {
    const existing = d.sessionMeta?.[sessionId]
    if (!existing || !awaitingName(existing) || existing.nameTriedAt) return
    existing.nameTriedAt = Date.now()
    marked = true
  })
  return marked
}
/**
 * Mark a session as used, without touching its title.
 *
 * The daemon hosts only the most recently updated sessions (project-host's
 * `MAX_HOSTED_SESSIONS`), and until this existed `updatedAt` moved on exactly
 * two events: CREATE and RENAME. So the registry's idea of "recency" was really
 * creation time, and the host window was the ten NEWEST sessions rather than the
 * ten LIVE ones. A session someone works in every day fell out of it the moment
 * ten newer ones existed, and stayed out: nothing attached it, so nothing
 * claimed its runs, so every turn sent from it sat at "waiting for a worker".
 * Not slowly, but forever, and invisibly from the daemon's side, because a doc
 * outside the window is never opened and so the queued run it is being blamed
 * for ignoring never even reaches its store.
 *
 * Sending a message is the signal that a session is in use, so that is what
 * moves this. Registry-only write, no session hydration: the same cheap
 * `sessionMeta` touch the session list already reads.
 *
 * A missing entry is FILLED IN rather than skipped. A session registered before
 * `sessionMeta` carried it would otherwise be permanently unhostable, which is
 * this same bug wearing a different hat.
 */
export async function touchSession(
  repo: Repo,
  projectId: string,
  sessionId: string,
  at: number = Date.now(),
): Promise<void> {
  const project = await findSessionCatalogForWrite(repo, projectId, sessionId)
  project.change((d) => {
    if (!d.sessionMeta) d.sessionMeta = {}
    // Mutate in place: re-assigning an existing doc object throws in Automerge.
    const existing = d.sessionMeta[sessionId]
    if (existing) {
      // Never move it BACKWARDS. Two surfaces can send on one session and their
      // clocks disagree; a late-arriving older write must not demote a session
      // that a newer one just promoted into the window.
      if ((existing.updatedAt ?? 0) < at) existing.updatedAt = at
      // `sentAt` is the clock the daemon's ingest watch compares against the
      // session log, so it moves here and nowhere else: a rename is use, not a
      // send, and must not read as a run that never arrived.
      if ((existing.sentAt ?? 0) < at) existing.sentAt = at
    } else {
      d.sessionMeta[sessionId] = { updatedAt: at, sentAt: at }
    }
  })
}
/**
 * Say what a session is DOING, on the registry (RFC 0284 S8a).
 *
 * The write half of `registryStatus`. Cheap on purpose: one entry on the
 * project doc, so a runtime can call it on every run transition without
 * opening anything, and a list can draw a hundred rows without opening
 * anything either.
 *
 * THE TIMESTAMP IS NOT OPTIONAL, even though the field is. A stored status with
 * no `statusAt` reads as unknown, by design, so writing one without the other
 * would be a claim nobody believes — which is safe, and silently useless. Both
 * are written together here so no caller can produce that.
 *
 * IDLE IS STILL WRITTEN rather than deleting the fields. Deleting means "no
 * runtime reports on this session", and a runtime that has just finished a run
 * knows more than that: it knows the session is quiet. The two read
 * differently and a list draws them differently.
 *
 * NOT MOVED BACKWARDS, like `touchSession` and for the same reason: two
 * runtimes can serve one session and their clocks disagree, so a late-arriving
 * older write must not overwrite a newer one's claim.
 */
export async function setSessionStatus(
  repo: Repo,
  projectId: string,
  sessionId: string,
  status: SessionRunStatus,
  at: number = Date.now(),
): Promise<void> {
  const project = await findSessionCatalogForWrite(repo, projectId, sessionId)
  project.change((d) => {
    if (!d.sessionMeta) d.sessionMeta = {}
    const existing = d.sessionMeta[sessionId]
    if (!existing) {
      d.sessionMeta[sessionId] = { status, statusAt: at }
      return
    }
    if ((existing.statusAt ?? 0) > at) return
    // A scalar still arrives as an object on reads; compare its value before
    // assigning so a re-stamp costs only the timestamp's one op.
    if (projectString(existing.status) !== status) existing.status = status
    existing.statusAt = at
  })
}
/**
 * File a session as done, or un-file it (RFC 0286).
 *
 * Writes `completedAt` on the registry entry ONLY, so filing costs the same
 * zero-hydration read the list already does and never opens the session doc.
 *
 * `updatedAt` is deliberately left alone. It means "when did this session last
 * do something", and filing is bookkeeping about the work rather than work:
 * bumping it would shuffle a session to the top of the list at the exact moment
 * a person said they were finished looking at it.
 */
export async function setSessionCompleted(
  repo: Repo,
  projectId: string,
  sessionId: string,
  completed: boolean,
  at: number = Date.now(),
): Promise<void> {
  const project = await findSessionCatalogForWrite(repo, projectId, sessionId)
  project.change((d) => {
    if (!d.sessionMeta) d.sessionMeta = {}
    // Mutate in place — re-assigning an existing doc object throws in Automerge.
    const existing = d.sessionMeta[sessionId]
    if (!existing) {
      if (completed) d.sessionMeta[sessionId] = { completedAt: at }
      return
    }
    if (completed) existing.completedAt = at
    else delete existing.completedAt
  })
}
/**
 * Delete a session by unregistering it from the project (remove from
 * `sessionIds` + `sessionMeta`). The session doc itself becomes unreachable from
 * the registry and is reclaimed by the local-store reachability GC — we do not
 * hard-delete it here (registry removal is the observable delete). Async: opens
 * the project handle.
 */
export async function deleteSession(
  repo: Repo,
  projectId: string,
  sessionId: string,
): Promise<void> {
  const project = await findSessionCatalogForWrite(repo, projectId, sessionId)
  project.change((d) => {
    if (d.sessionIds) {
      const i = d.sessionIds.findIndex((id) => projectString(id) === sessionId)
      if (i !== -1) d.sessionIds.splice(i, 1)
    }
    if (d.sessionMeta) delete d.sessionMeta[sessionId]
  })
}
/**
 * Move a session from one project's registry to another (RFC 0290 S6).
 *
 * A project's registry IS where a session works, today. RFC 0279 §4 specifies a
 * declaration (`SessionAttachedData.workspaceId`) and a World-level fold over
 * it, and neither exists in code, so the registries are not a cache of anything
 * yet: they are the record. The daemon's `projectOf` reads them and so does the
 * inbox, which is why moving between them is the whole of moving a session.
 *
 * TARGET FIRST, SOURCE SECOND, and the order is not stylistic. Between the two
 * writes the session is listed twice, which shows as one duplicate row for the
 * length of a sync. The other order lists it NOWHERE, and a session in no
 * registry is exactly what `deleteSession` above produces on purpose: the local
 * store's reachability GC is entitled to collect it. A duplicate that resolves
 * costs a frame. A collected session doc costs the conversation.
 *
 * The meta entry is CARRIED, not re-minted. Re-minting would reset the title to
 * a default and drop `sourceId`, so a moved session would lose the link to the
 * conversation it was imported from and reappear as "New Session".
 *
 * A move to the project it is already in is a no-op rather than a delete
 * followed by an insert, because those two writes against one document would
 * remove the session and put it back with a different meta entry.
 */
export async function moveSession(
  repo: Repo,
  sessionId: string,
  fromProjectId: string,
  toProjectId: string,
): Promise<void> {
  if (fromProjectId === toProjectId) return
  if (usesPrivateSessionCatalog(repo))
    return movePrivateSessionCatalog(
      repo,
      sessionId,
      fromProjectId,
      toProjectId,
    )

  const from = await findProjectForWrite(repo, fromProjectId)
  if (from.doc().sharingGroupId)
    throw new Error('Moving private sessions requires the private catalog')
  const carried: SessionMetaEntry = {
    ...(from.doc()?.sessionMeta?.[sessionId] ?? {}),
  }

  const to = await findProjectForWrite(repo, toProjectId)
  if (to.doc().sharingGroupId)
    throw new Error('Moving private sessions requires the private catalog')
  const carriedComments = Object.fromEntries(
    Object.entries(
      mapWithConflicts<CommentIndexEntry>(from.doc(), 'commentIndex'),
    )
      .filter(([, entry]) => String(entry.sessionId ?? '') === sessionId)
      .map(([id, entry]) => [id, JSON.parse(JSON.stringify(entry))]),
  ) as Record<string, CommentIndexEntry>
  await writeCommentIndex(repo, toProjectId, carriedComments)
  changeProject(to, (d) => {
    if (!d.sessionIds) d.sessionIds = []
    if (!d.sessionIds.some((id) => projectString(id) === sessionId))
      d.sessionIds.push(sessionId)
    if (!d.sessionMeta) d.sessionMeta = {}
    // updatedAt moves with it: the session is as recent in its new home as it
    // was in its old one, and a registry that says otherwise sorts it wrong.
    d.sessionMeta[sessionId] = { ...carried }
  })

  changeProject(from, (d) => {
    if (d.sessionIds) {
      const i = d.sessionIds.findIndex((id) => projectString(id) === sessionId)
      if (i !== -1) d.sessionIds.splice(i, 1)
    }
    if (d.sessionMeta) delete d.sessionMeta[sessionId]
  })
  removeCommentIndexEntries(from, Object.keys(carriedComments))
}
/**
 * Append a message to a session's inline log. Opens the SESSION handle and wraps
 * it in `BranchLog` (which reads/writes the doc's `thread` key) — the same doc,
 * never a separate log doc. Returns the new entry id.
 */
export async function appendMessage(
  repo: Repo,
  sessionId: string,
  message: MessageInput,
): Promise<EntryId> {
  const handle = await repo.find<SessionDoc>(sessionId as AnyDocumentId)
  // BranchLog wraps a DocHandle<LogDoc>; the session doc carries the log under
  // `thread`, so the cast is the legacy-faithful shape (`new BranchLog(handle)`).
  const log = new BranchLog(handle as never)
  return log.append(messagePayload(message), log.heads())
}
/**
 * Append MANY messages to a session while keeping the inline thread BOUNDED via
 * incremental sealing — the native paginated ingest path (design/session-thread-
 * storage.md, Option C). Importing a large captured transcript with per-message
 * `appendMessage` builds one multi-MB inline doc that neither syncs nor hydrates
 * reliably over subduction (the reader panics, the writer partial-syncs). Here we
 * hold ONE session handle, append, and every `sealEvery` messages drain the
 * `ThreadSealer` so the oldest entries seal out into small page docs — the inline
 * tail never grows past ~`highWater`, and the huge doc is never built. Reads
 * reunite tail + pages via `projectSessionTranscriptPaged`.
 *
 * Sealing needs paging enabled AND the session's `createdBy` (set by
 * `createSession`); if paging is off it degrades to a plain batched append.
 * Returns the appended entry ids in order.
 */
export async function appendMessages(
  repo: Repo,
  sessionId: string,
  messages: ReadonlyArray<MessageInput>,
): Promise<EntryId[]> {
  if (messages.length === 0) return []
  const handle = await repo.find<SessionDoc>(sessionId as AnyDocumentId)

  const ids: EntryId[] = []
  let fallbackClock = Date.now()
  // Write EVERY entry in ONE Automerge change, not one change per message: keeping
  // a single-doc session's op-history compact is the whole point, and N changes
  // bloat it needlessly (each carries its own actor/seq/hash/deps). Each entry
  // carries its (deterministic-when-provided) id + createdAt and NO parents —
  // entries order by `(createdAt, id)`, so a re-import with the same ids overwrites
  // in place (idempotent) rather than appending duplicates. RFC 0258: sessions are
  // single-doc (capped), so this is a plain inline append — no paging.
  handle.change((d: Draft) => {
    if (!d[THREAD_KEY]) initThread(d)
    for (const m of messages) {
      const id = m.id ?? uuidv4()
      const createdAt = m.createdAt ?? (fallbackClock++ as TimestampMs)
      writeEntry(d[THREAD_KEY], id, [], createdAt, messagePayload(m))
      ids.push(id)
    }
  })
  return ids
}
function messagePayload(m: MessageInput): EntryPayload {
  return {
    type: 'message',
    payload: {
      role: m.role,
      status: 'completed',
      text: m.text,
      toolUses: m.toolUses ?? [],
      ...(m.toolUseId ? { toolUseId: m.toolUseId } : {}),
      ...(m.isError ? { isError: m.isError } : {}),
      ...(m.writtenOn ? { metadata: { ...m.writtenOn } } : {}),
      ...(m.voiceCallId
        ? { voiceCallId: m.voiceCallId, spokenBy: m.spokenBy ?? 'person' }
        : {}),
    },
  }
}
/**
 * Mirror what a session holds onto the registry (RFC 0311 3d).
 *
 * The write half of `projectDocumentHolders`. Called by the host at run
 * transitions, with `projectHoldings` in recency order; capped here so no
 * caller can grow the entry. Returns whether it wrote: an unchanged list is
 * NOT written, so a turn that holds what the last one held costs nothing.
 */
export async function setSessionHolding(
  repo: Repo,
  projectId: string,
  sessionId: string,
  documentIds: readonly string[],
): Promise<boolean> {
  const next = documentIds.slice(0, HOLDING_MAX).map(String)
  let wrote = false
  const project = await findSessionCatalogForWrite(repo, projectId, sessionId)
  project.change((d) => {
    // No entry, no write. A session deleted or moved away mid-run is still
    // hosted until its run ends, and creating an entry for it here would
    // leave a ghost row in a header, one that opens a session that is gone.
    const existing = d.sessionMeta?.[sessionId]
    if (!existing) return
    const prev = (existing.holding ?? []).map(String)
    if (prev.length === next.length && prev.every((id, i) => id === next[i]))
      return
    existing.holding = next
    wrote = true
  })
  return wrote
}

/**
 * Add a project to a session (RFC 0362 3c): its instructions, documents and
 * sources reach the session's next turn. The home project, which lists the
 * session, is never added; adding one twice is a no-op. Returns whether the
 * list changed.
 */
export async function addSessionProject(
  repo: Repo,
  sessionId: string,
  homeProjectId: string,
  projectId: string,
): Promise<boolean> {
  const id = projectId.replace(/^automerge:/, '')
  if (!id) throw new Error('no project to add')
  if (id === homeProjectId.replace(/^automerge:/, ''))
    throw new Error("the home project is already the session's")
  const handle = await repo.find<SessionDoc>(sessionId as AnyDocumentId)
  await (handle as { whenReady?: () => Promise<void> }).whenReady?.()
  if (sessionAddedProjects(handle.doc()).includes(id)) return false
  let added = false
  handle.change((d) => {
    // Checked again inside the change: two adds at once must not store it
    // twice on this peer. Two peers can still both add; the read dedupes.
    if (!d.addedProjectIds) d.addedProjectIds = []
    if (d.addedProjectIds.some((p) => String(p) === id)) return
    d.addedProjectIds.push(id)
    added = true
  })
  return added
}

/** Take a project off a session's added list; returns whether it was there. */
export async function removeSessionProject(
  repo: Repo,
  sessionId: string,
  projectId: string,
): Promise<boolean> {
  const id = projectId.replace(/^automerge:/, '')
  const handle = await repo.find<SessionDoc>(sessionId as AnyDocumentId)
  await (handle as { whenReady?: () => Promise<void> }).whenReady?.()
  const at = sessionAddedProjects(handle.doc()).indexOf(id)
  if (at < 0) return false
  handle.change((d) => {
    const list = d.addedProjectIds ?? []
    // Every copy: two peers adding at once can leave the id twice.
    for (let i = list.length - 1; i >= 0; i--)
      if (String(list[i]) === id) list.splice(i, 1)
    if (list.length === 0) delete d.addedProjectIds
  })
  return true
}
